import * as React from 'react';
import { useCallback, useRef, useEffect, useState } from 'react';
import { Box, BoxProps, Collapse, IconButton, List, ListItem, ListItemText, useTheme } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Message } from '../io/Api';
import { fuzzyParseJSON } from '../hooks/useBedrock';

interface Props extends BoxProps {
    messages: Message[];
    typing: boolean;
}

const JsonMessage = ({ data }: { data: { [k: string]: unknown } }): JSX.Element => {
    const [expanded, setExpanded] = useState(!data['text']);
    const toggle = useCallback(() => {
        setExpanded(!expanded);
    }, [expanded])
    return <>
        <ListItemText>{(data['text']||'')+''}
        <Collapse in={expanded} timeout='auto' collapsedSize={40} sx={{position: 'relative'}}>
            <IconButton onClick={toggle} sx={{position: 'absolute', right: '0'}}>
                {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon />}
            </IconButton>
        <pre>{JSON.stringify(Object.fromEntries(Object.entries(data).filter(([k]) => k !== 'text')), undefined, 2)}</pre>
        </Collapse>
        </ListItemText>
    </>
}

const ChatView = ({ messages, typing, sx, ...props }: Props): JSX.Element => {

    const ref = useRef<HTMLUListElement>(null);
    const theme = useTheme();

    useEffect(() => {
        ref.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }, [messages])

    return (
        <Box sx={{ ...sx, overflowY: 'scroll' }} {...props}>
            <List ref={ref} sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                'li>div': {
                    borderRadius: '4px',
                    background: theme.palette.secondary.main+'30',
                    color: theme.palette.primary.contrastText,
                    padding: '8px',
                    flexGrow: 0,
                    minWidth: '40%',
                    maxWidth: '75%'
                },
                '.role-assistant': {
                    flexDirection: 'row-reverse',
                    '&>div': {
                        background: theme.palette.primary.main+'30',
                    },
                    '.par': {
                        marginTop: '8px'
                    }
                }
            }} >
                {messages.map((msg, index) => (
                    <ListItem key={index} className={`role-${msg.role}`}>
                        {msg.text.trim().startsWith('{')
                            ? <JsonMessage data={fuzzyParseJSON(msg.text)} />
                            : <ListItemText>{msg.text.split('\n').map((text, i) => (
                                <div className='par' key={i}>{text}</div>
                            ))}</ListItemText>
                        }
                    </ListItem>
                ))}
                {typing && (
                    <ListItem className='role-assistant'>
                        <ListItemText sx={{ fontStyle: 'italic', opacity: '50%' }}>typing...</ListItemText>
                    </ListItem>
                )}
            </List>
        </Box>
    );
}

export default ChatView;